import { useEffect, useId, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { TbSearch, TbUserPlus, TbExternalLink, TbArrowsExchange, TbAlertTriangle, TbMail, TbMapPin, TbLoader2 } from "react-icons/tb";
import CreatePatientModal from "src/components/CreatePatientModal/CreatePatientModal";
import { ConsentRequest } from "./ConsentRequest";
import type { Patient, Recipient } from "src/types";
import {
    consentStates,
    deliveryGaps,
    formatBirthDate,
    nameFromQuery,
    patientName,
    searchPatients,
} from "./patientSearch";

/** Step 1: pick the patient (search, or create a new one). The recipient — who pays — comes with
 *  the patient's record; its contact data is shown read-only, missing consent can be requested
 *  right here (story 11). */
export function StepPatient({
    patientId,
    onSelect,
    patients,
    patientsLoading,
    onPatientsChanged,
    recipient,
    recipientLoading,
    onRecipientChanged,
    consentRequired,
}: {
    patientId: string | null;
    onSelect: (patientId: string | null) => void;
    patients: Patient[];
    patientsLoading: boolean;
    /** Re-fetch the patient list (after creating one); resolves once it's fresh. */
    onPatientsChanged: () => Promise<void>;
    /** The selected patient's recipient — loaded once by the wizard, shared with the check step. */
    recipient: Recipient | null;
    recipientLoading: boolean;
    onRecipientChanged: () => Promise<void>;
    consentRequired: boolean;
}) {
    const selected = patients.find((p) => p.id === patientId) ?? null;

    if (patientId && selected) {
        return (
            <SelectedPatient
                patient={selected}
                onChange={() => onSelect(null)}
                recipient={recipient}
                loading={recipientLoading}
                refetch={onRecipientChanged}
                consentRequired={consentRequired}
            />
        );
    }
    return (
        <PatientPicker
            patients={patients}
            loading={patientsLoading}
            // A patient just created may not be in the list yet — select it once the list has it.
            pendingId={patientId && !selected ? patientId : null}
            onSelect={onSelect}
            onPatientsChanged={onPatientsChanged}
        />
    );
}

function PatientPicker({
    patients,
    loading,
    pendingId,
    onSelect,
    onPatientsChanged,
}: {
    patients: Patient[];
    loading: boolean;
    pendingId: string | null;
    onSelect: (patientId: string | null) => void;
    onPatientsChanged: () => Promise<void>;
}) {
    const { t } = useTranslation();
    const listId = useId();
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const [creating, setCreating] = useState(false);

    const results = useMemo(() => searchPatients(patients, query), [patients, query]);
    useEffect(() => setActive(0), [query]);

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (results.length === 0) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((i) => Math.min(i + 1, results.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            onSelect(results[active].id);
        }
    };

    const searching = query.trim().length > 0;

    return (
        <div className="ic-patient">
            <div className="ic-patient__search">
                <TbSearch aria-hidden className="ic-patient__search-icon" />
                <input
                    type="search"
                    className="ic-patient__search-input"
                    placeholder={t("invoiceCreate.patient.searchPlaceholder")}
                    aria-label={t("invoiceCreate.patient.searchPlaceholder")}
                    value={query}
                    autoFocus
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onKeyDown}
                    role="combobox"
                    aria-expanded={results.length > 0}
                    aria-controls={listId}
                    aria-activedescendant={results.length > 0 ? `${listId}-${active}` : undefined}
                    autoComplete="off"
                />
                {(loading || pendingId) && <TbLoader2 className="ic-patient__search-spinner" aria-label={t("invoiceCreate.patient.loading")} />}
            </div>

            {results.length > 0 && (
                <ul id={listId} role="listbox" className="ic-patient__results" aria-label={t("invoiceCreate.patient.results")}>
                    {results.map((p, i) => {
                        const payer = p.recipient && p.recipient.sameAsPatientId !== p.id ? patientName(p.recipient) : null;
                        return (
                            <li
                                key={p.id}
                                id={`${listId}-${i}`}
                                role="option"
                                aria-selected={i === active}
                                className={`ic-patient__result${i === active ? " ic-patient__result--active" : ""}`}
                                onMouseEnter={() => setActive(i)}
                                onClick={() => onSelect(p.id)}
                            >
                                <span className="ic-patient__result-name">{patientName(p)}</span>
                                <span className="ic-patient__result-meta">
                                    {formatBirthDate(p.birthDate)}
                                    {payer && <> · {t("invoiceCreate.patient.paidBy", { name: payer })}</>}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            )}

            {searching && results.length === 0 && !loading && (
                <p className="ic-patient__empty">{t("invoiceCreate.patient.noResults", { query: query.trim() })}</p>
            )}
            {!searching && <p className="ic-patient__hint">{t("invoiceCreate.patient.searchHint")}</p>}

            <button type="button" className="ic-patient__create" onClick={() => setCreating(true)}>
                <TbUserPlus aria-hidden />
                <span>
                    <strong>{t("invoiceCreate.patient.createNew")}</strong>
                    <small>{t("invoiceCreate.patient.createNewHint")}</small>
                </span>
            </button>

            {creating &&
                createPortal(
                    <CreatePatientModal
                        prefill={nameFromQuery(query)}
                        onClose={() => setCreating(false)}
                        onUseExisting={(p) => onSelect(p.id)}
                        onSaved={async (newId) => {
                            if (!newId) return;
                            onSelect(newId);
                            await onPatientsChanged();
                        }}
                    />,
                    document.body,
                )}
        </div>
    );
}

function SelectedPatient({ patient, onChange, recipient, loading, refetch, consentRequired }: {
    patient: Patient;
    onChange: () => void;
    recipient: Recipient | null;
    loading: boolean;
    refetch: () => Promise<void>;
    consentRequired: boolean;
}) {
    const { t } = useTranslation();
    const recipientId = patient.recipient?.id ?? null;

    // Contact data or consent may be fixed in the patient record (opened in a new tab) — pick up
    // the changes when the user comes back.
    useEffect(() => {
        if (!recipientId) return;
        const onFocus = () => void refetch();
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [recipientId, refetch]);

    const isSelf = !!recipient && recipient.sameAsPatientId === patient.id;
    const gaps = recipient ? deliveryGaps(recipient) : null;
    // For this patient — a child added after the parent signed isn't covered by that consent.
    const consent = recipient ? consentStates(recipient, patient.id) : null;

    return (
        <div className="ic-selected">
            <div className="ic-selected__patient">
                <div className="ic-selected__avatar" aria-hidden>
                    {(patient.firstName?.[0] ?? "") + (patient.lastName?.[0] ?? "")}
                </div>
                <div className="ic-selected__who">
                    <span className="ic-selected__name">{patientName(patient)}</span>
                    <span className="ic-selected__meta">{t("invoiceCreate.patient.born", { date: formatBirthDate(patient.birthDate) })}</span>
                </div>
                <button type="button" className="ic-selected__change" onClick={onChange}>
                    <TbArrowsExchange aria-hidden /> {t("invoiceCreate.patient.changePatient")}
                </button>
            </div>

            {!recipientId ? (
                <div className="ic-note ic-note--warn" role="status">
                    <TbAlertTriangle aria-hidden />
                    <span>{t("invoiceCreate.patient.noRecipient")}</span>
                </div>
            ) : loading || !recipient ? (
                <div className="ic-selected__loading">
                    <TbLoader2 aria-hidden /> {t("invoiceCreate.patient.loadingRecipient")}
                </div>
            ) : (
                <div className="ic-selected__recipient">
                    <div className="ic-selected__section">
                        <span className="ic-selected__label">{t("invoiceCreate.patient.recipient")}</span>
                        <span className="ic-selected__value">
                            {patientName(recipient)}
                            {isSelf && <span className="ic-selected__tag">{t("invoiceCreate.patient.self")}</span>}
                        </span>
                        <ul className="ic-selected__contact">
                            <li className={gaps!.address ? "ic-selected__contact--missing" : undefined}>
                                <TbMapPin aria-hidden />
                                {gaps!.address
                                    ? t("invoiceCreate.patient.noAddress")
                                    : `${recipient.street}, ${recipient.plz} ${recipient.city}`}
                            </li>
                            <li className={gaps!.email ? "ic-selected__contact--missing" : undefined}>
                                <TbMail aria-hidden />
                                {gaps!.email ? t("invoiceCreate.patient.noEmail") : recipient.email}
                            </li>
                        </ul>
                    </div>

                    <div className="ic-selected__section">
                        <span className="ic-selected__label">{t("invoiceCreate.patient.consent")}</span>
                        {consentRequired ? (
                            <div className="ic-selected__chips">
                                <span className={`chip ${consent!.digital === "signed" ? "chip--success" : consent!.digital === "requested" ? "chip--info" : "chip--warning"}`}>
                                    {t("recipientPage.digitalSendingLabel")}: {t(`invoiceCreate.patient.consentDigital.${consent!.digital}`)}
                                </span>
                                <span className={`chip ${consent!.post === "missing" ? "chip--warning" : "chip--success"}`}>
                                    {t("recipientPage.postConsentLabel")}: {t(`invoiceCreate.patient.consentPost.${consent!.post}`)}
                                </span>
                            </div>
                        ) : (
                            <span className="ic-selected__value ic-selected__value--muted">{t("invoiceCreate.patient.consentNotRequired")}</span>
                        )}
                        <ConsentRequest recipient={recipient} patient={patient} required={consentRequired} onChanged={refetch} />
                    </div>

                    {gaps!.email && gaps!.address && (
                        <div className="ic-note ic-note--warn" role="status">
                            <TbAlertTriangle aria-hidden />
                            <span>{t("invoiceCreate.patient.noDeliveryWay")}</span>
                        </div>
                    )}

                    <a className="ic-selected__record-link" href={`/patients/${recipient.id}`} target="_blank" rel="noreferrer">
                        {t("invoiceCreate.patient.openRecord")} <TbExternalLink aria-hidden />
                    </a>
                </div>
            )}
        </div>
    );
}

/** Text for the step's card in the overview row. */
export function patientSummary(patient: Patient | null, t: TFunction) {
    if (!patient) return null;
    const payer = patient.recipient && patient.recipient.sameAsPatientId !== patient.id ? patientName(patient.recipient) : null;
    return (
        <>
            <strong className="ic-card__strong">{patientName(patient)}</strong>
            <span>{formatBirthDate(patient.birthDate)}</span>
            {payer && <span>{t("invoiceCreate.patient.paidBy", { name: payer })}</span>}
        </>
    );
}
